package co.torvus.web.sections

import co.torvus.web.classes
import co.torvus.web.components.DottedBackground
import co.torvus.web.components.DownloadBadges
import co.torvus.web.content.homeSlides
import js.objects.unsafeJso
import js.reflect.unsafeCast
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.useRef
import react.useState
import web.cssom.ClassName
import web.dom.ElementId
import web.html.HTMLDivElement

// Fraction of the slide width the finger must travel (on release) to change slides.
private const val SWIPE_RATIO = 0.2

val HomeSection = FC<Props> {
    var page by useState(0)
    val touchStartX = useRef<Double>()
    val trackRef = useRef<HTMLDivElement>()
    val lastIndex = homeSlides.size - 1

    section {
        className = ClassName("home section")
        id = ElementId("home")

        div {
            className = ClassName("home-hero")

            DottedBackground {}

            div {
                className = ClassName("home-viewport")
                onTouchStart = { event ->
                    touchStartX.current = event.asDynamic().changedTouches[0].clientX as Double
                    trackRef.current?.let { it.asDynamic().style.transition = "none" }
                }
                onTouchMove = { event ->
                    val start = touchStartX.current
                    val track = trackRef.current
                    if (start != null && track != null) {
                        val width = track.asDynamic().offsetWidth as Double
                        var delta = (event.asDynamic().touches[0].clientX as Double) - start
                        if ((page == 0 && delta > 0) || (page == lastIndex && delta < 0)) {
                            delta /= 3.0 // rubber-band resistance at the ends
                        }
                        track.asDynamic().style.transform = "translateX(${-page * width + delta}px)"
                    }
                }
                onTouchEnd = { event ->
                    val start = touchStartX.current
                    val track = trackRef.current
                    if (start != null && track != null) {
                        val width = track.asDynamic().offsetWidth as Double
                        val delta = (event.asDynamic().changedTouches[0].clientX as Double) - start
                        val threshold = width * SWIPE_RATIO
                        val target = when {
                            delta < -threshold && page < lastIndex -> page + 1
                            delta > threshold && page > 0 -> page - 1
                            else -> page
                        }
                        // restore the CSS transition, then snap to the target slide
                        track.asDynamic().style.transition = ""
                        track.asDynamic().style.transform = "translateX(${-100 * target}%)"
                        page = target
                    }
                    touchStartX.current = null
                }
                onTouchCancel = { _ ->
                    trackRef.current?.let {
                        it.asDynamic().style.transition = ""
                        it.asDynamic().style.transform = "translateX(${-100 * page}%)"
                    }
                    touchStartX.current = null
                }

                div {
                    className = ClassName("home-track")
                    ref = trackRef
                    style = unsafeJso {
                        transform = unsafeCast("translateX(${-100 * page}%)")
                    }

                    homeSlides.forEach { slide ->
                        div {
                            className = ClassName("home-slide")
                            slide.blocks.forEach { block ->
                                div {
                                    className = ClassName("home-block")
                                    if (slide.hero) {
                                        h1 {
                                            className = ClassName("home-hero-title")
                                            +block.title
                                        }
                                        block.paragraphs.forEach { para ->
                                            p {
                                                className = ClassName("home-hero-body")
                                                +para
                                            }
                                        }
                                    } else {
                                        h2 {
                                            className = ClassName("home-block-title")
                                            +block.title
                                        }
                                        block.paragraphs.forEach { para ->
                                            p {
                                                className = ClassName("home-block-body")
                                                +para
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (page > 0) {
                button {
                    className = ClassName("home-chevron left")
                    onClick = { page -= 1 }
                    span { className = ClassName("chevron left") }
                }
            }
            if (page < lastIndex) {
                button {
                    className = ClassName("home-chevron right")
                    onClick = { page += 1 }
                    span { className = ClassName("chevron right") }
                }
            }

            div {
                className = ClassName("home-dots")
                homeSlides.forEachIndexed { index, _ ->
                    span {
                        className = classes("home-dot", if (index == page) "active" else null)
                    }
                }
            }
        }

        DownloadBadges {}
    }
}
